
import {
  computed,
  defineComponent,
  PropType,
  watch,
  onMounted,
  reactive,
  toRefs,
  ref,
  ComponentOptions,
  onUpdated
} from 'vue'

import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
// import ListHeader from '@/components/ListHeader.vue'
import { Entry, EntryConfig, RequestHandler, UUID } from '@Protium/vue-ozone-utils'
import EntryColumn, { ColumnView } from './EntryColumn.vue'
import ListHeader from '@/components/ListHeader.vue'
import SelectHeader from '@/components/SelectHeader.vue'
import { ItemViewMode } from '@/constants'
import { forEach, toNumber } from 'lodash'
import { debounce } from '@/utils'
import useDeviceSettings from '@/composables/useDeviceSettings'

interface EntryColumnConfig {
  itemSize?: string
}

enum ListEvent {
  add = 'add',
  remove = 'remove',
  select = 'select',
  search = 'search'
}

export enum GroupView {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
  Dropdown = 'dropdown'
}

enum GroupSort {
  AlphaNumeric = 'alphanumeric'
}

interface Group {
  [key: string]: Array<Entry>
}

export default defineComponent({
  name: 'ColumnGroup',
  components: { ListHeader, SelectHeader, EntryColumn },
  props: {
    minWidth: {
      type: Number,
      default: 380
    },
    width: {
      type: String
    },
    colMinWidth: {
      type: [String, Number]
    },
    colMaxWidth: {
      type: [String, Number]
    },
    colWidth: {
      type: String
    },
    type: {
      type: String
    },
    sort: {
      type: String as PropType<GroupSort>,
      default: GroupSort.AlphaNumeric
    },
    groups: {
      type: Object as PropType<Group>,
      required: true
    },
    view: {
      type: String as PropType<GroupView>,
      default: GroupView.Vertical
    },
    columnView: {
      type: String as PropType<ColumnView>,
      default: ColumnView.Vertical
    },
    itemView: {
      type: Number as PropType<ItemViewMode>,
      default: ItemViewMode.Default
    },
    config: {
      type: Object as PropType<EntryConfig>
    },
    itemConfig: {
      type: Object
    },
    classList: {
      type: [String, Array],
      default: ''
    },
    request: {
      type: Object as PropType<RequestHandler<Entry>>
    },
    selection: {
      type: Array,
      default: () => ([])
    },
    expandable: {
      type: Boolean,
      default: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    paginate: {
      type: Number,
      default: 0
    },
  },
  emits: ['active-group', 'active', 'select', 'add', 'remove', 'search', 'resolved', 'update:modelValue'],
  setup (props, { emit }) {
    //How much of the column should be visible for the column button to be highlighted
    const COLUMN_HIGHLIGHT_PERCENTAGE = 0.9
    const SCROLL_OFFSET = 0

    const columnsContainer = ref<HTMLElement>()
    const columns = ref<Array<ComponentOptions>>([])
    const $store = useStore()

    const activeGroup = ref<string>()
    const activeView = computed<GroupView>(() => props.view || GroupView.Vertical)
    const activeColumn = ref<Element>()
    const showSelect = ref<boolean>(false)
    // const columnNavHighlight = ref<Array<boolean>>([])
    const offsetIndex = ref<number>(0)
    const sortedGroupKeys = computed(() => !props.sort ? Object.keys(props.groups || {}) : Object.keys(props.groups || {}).sort((x, y) => x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' })))

    watch(() => props.groups, (payload) => {

      if (payload) {
        activeGroup.value = Object.keys(payload)[0]
        emit('active-group', activeGroup.value)
      }

      columns.value = Object.keys(payload).map((key, index) => {
        return {
          active: false,
          label: key,
          // TODO: fix element navigation once filter is changed
          element: columnsContainer.value?.children[index],
        }
      })
      updateSelection(sortedGroupKeys.value[0])
      offsetIndex.value = 0
      columnSelector(0)
      updateSelection(sortedGroupKeys.value[0])
      columnsContainer.value?.addEventListener('scroll', debounce(checkVisibility, 500))
    })

    // HOTFIX: on Dashboard/Orders/Overview the offset of the order group columns must be updated when the dashboard is collapsed/expanded
    watch(() => $store.getters['User/Settings']?.hideOperationalDashboard, (v) => {
      columnContainerBounds.value = columnsContainer.value?.getBoundingClientRect()
    })


    // onMounted(() => {
    //   if (props.groups) activeGroup.value = Object.keys(props.groups)[0]
    // })

    function setActive (group: string) {
      // activeGroup.value = group
      // showSelect.value = false
      emit('active-group', activeGroup.value)
    }

    onMounted(() => {
      columnContainerBounds.value = columnsContainer.value?.getBoundingClientRect()
      checkVisibility()
    })
    onUpdated(() => {
      columnContainerBounds.value = columnsContainer.value?.getBoundingClientRect()
      // checkVisibility()
    })
    const columnContainerBounds = ref(columnsContainer.value?.getBoundingClientRect())
    const columnsContainerOffset = computed(() => (columnContainerBounds.value?.top || 0))

    function columnSelector (index: number) {
      if (!columns.value) {
        console.warn('Columns ref was undefined')
        return
      }
      const el: Element = columns.value[index]?.element?.$el
      const container = columnsContainer?.value

      if (!el || !container) {
        // console.warn(container ? `Column element at index ${index} was undefined` : 'Container ref was undefined', columns.value)
        return
      }

      const top = (el.getBoundingClientRect().top - container.getBoundingClientRect().top) + SCROLL_OFFSET + container.scrollTop
      const left = (el.getBoundingClientRect().left - container.getBoundingClientRect().left) + SCROLL_OFFSET + container.scrollLeft

      container?.scrollTo({ behavior: 'smooth', top, left })
      // el.scrollIntoView({behavior: 'smooth', inline: 'start'})
    }

    function checkVisibility () {
      const container = columnsContainer.value
      if (!container) {
        console.warn('Containing element .columns-container was not found')
        return
      }
      columns.value.forEach((column, index) => {
        columns.value[index].active = isColumnInParentViewport(columns.value[index], container)
      })
    }

    function isColumnInParentViewport (column, parent) {
      const child = column?.element?.$el
      if (!child) {
        return
      }

      const childBounds = child.getBoundingClientRect()
      const parentBounds = parent.getBoundingClientRect()
      if (childBounds.x >= parentBounds.x) return (parseInt(childBounds.x) + parseInt(childBounds.width)) * COLUMN_HIGHLIGHT_PERCENTAGE <= parseInt(parentBounds.x) + parseInt(parentBounds.width)
      return false
    }

    function decreaseOffsetIndex () {
      if (offsetIndex.value > 0) {
        offsetIndex.value -= 1
        // columnSelector(offsetIndex.value)
        updateSelection(sortedGroupKeys.value[offsetIndex.value])
      }
    }

    function increaseOffsetIndex () {
      if (offsetIndex.value < sortedGroupKeys.value.length - 1) {
        offsetIndex.value++
        // columnSelector(offsetIndex.value)
        updateSelection(sortedGroupKeys.value[offsetIndex.value])
      }
    }

    function updateSelection (value) {
      const index = sortedGroupKeys.value.indexOf(value)
      offsetIndex.value = index < 0 ? 0 : (index > sortedGroupKeys.value.length - 1 ? (sortedGroupKeys.value.length - 1) : index)
      columnSelector(offsetIndex.value)

      activeGroup.value = value
      setActive(value)
    }

    onMounted(() => {
      //Initially setting the navigation to start
      // offsetIndex.value = 0
      // columnSelector(0)
      activeGroup.value = Object.keys(props.groups || {})[offsetIndex.value]
      updateSelection(sortedGroupKeys.value[0])
      columnsContainer.value?.addEventListener('scroll', debounce(checkVisibility, 500))
    })

    return {
      requestHandler: props.request || $store.getters['Services/Directr'],
      //sortedGroupKeys: computed(() => !props.sort ? Object.keys(props.groups || {}) : Object.keys(props.groups || {}).sort((x, y) => x.localeCompare(y, undefined, { numeric: true, sensitivity: 'base' }))),
      sortedGroupKeys,
      contentType: computed(() => props.type || ((((props.groups[Object.keys(props.groups)[0] || ''] || [])[0] || {}).sys) || {}).type || 'unknown'),
      // subtitle: computed(() => `${Object.keys(props.groups).length} ${props.type} groups`),
      emit: (type: ListEvent, $event) => emit(type, $event),
      addColumnRef: (label, e) => {
        if (!e) return
        if (!label) return
        const match = columns.value.find(c => c.label === label)

        if (!(e.$el)) throw Error('e was not a component reference')

        const top = e.$el.getBoundingClientRect().top
        const left = e.$el.getBoundingClientRect().left

        if (!match) {
          columns.value.push({ element: e, top, left, active: false, label })
          columns.value.sort((x, y) => x.left > y.left ? 1 : -1)
        } else if (!match.element) {
          match.element = e
          match.top = top
          match.left = left
          columns.value.sort((x, y) => x.left > y.left ? 1 : -1)
        }
      },
      ColumnView,
      ItemViewMode,
      GroupView,
      activeView,
      activeGroup,
      activeColumn,
      showSelect,
      setActive,
      columnsContainer,
      columnsContainerOffset,
      columns,
      columnSelector,
      offsetIndex,
      increaseOffsetIndex,
      decreaseOffsetIndex,
      updateSelection,
      overviewPageWidth: (window.innerWidth - 230), //230px is the width of sidebar
      orderContainerWidth: 490 //490px is the width of the order container
    }
  }
})
